/**
 * Revalidates all SWR cache entries that match a given filter function.
 *
 * SWR (stale-while-revalidate) stores response data in a global cache keyed by unique identifiers.
 * This utility function allows you to find and manually revalidate multiple related cache entries
 * — for example, paginated data views like "orders-page-0", "orders-page-1", etc.
 *
 * 🧠 Why use this?
 * ----------------
 * In SWR, you normally revalidate like this:
 *     mutate("my-cache-key")
 *
 * But when you use **multiple keys** (like paginated or filtered views),
 * you may need to invalidate ALL related entries at once.
 * This function helps do that — **dynamically** and **safely**.
 *
 * ⚠️ This is an advanced pattern.
 * For most cases, revalidating a single SWR key is enough.
 * For a simpler pattern, see `useSkipDaysQuery`, which uses `mutate(SWR_KEY)` directly.
 *
 * @param cache - The SWR global cache instance (from `useSWRConfig().cache`)
 * @param mutate - The SWR mutate function (from `useSWRConfig().mutate`)
 * @param filterFn - A function that returns `true` for keys that should be revalidated
 * @param revalidate - Whether to revalidate the data from the server (defaults to true)
 *
 * @example
 *   import { useSWRConfig } from "swr";
 *   import { filterOrdersKeys } from "./swr-utils";
 *   import { refreshMultipleCaches } from "./refresh-utils";
 *
 *   const { cache, mutate } = useSWRConfig();
 *   refreshMultipleCaches(cache, mutate, filterOrdersKeys);
 */
export const refreshMultipleCaches = (cache, mutate, filterFn, revalidate = true) => {
    // Extract all keys currently stored in SWR cache
    const allKeys = Array.from(cache.keys());
    // Use the provided filter to isolate only relevant keys
    const matchingKeys = allKeys.filter(filterFn);
    // Revalidate each matching key individually
    matchingKeys.forEach((key) => {
        mutate(key, undefined, { revalidate });
    });
};
